import { AwsS3Service } from "src/app/services/common/aws-s3.service";
import { GeoTaggedFrame } from "./gopro-metadata-extractor";
import { Injectable } from "@angular/core";
import {
  CriticalPoint,
  CriticalPointAssignment,
  PathEditState,
  PracticePath,
  PracticePathBaseInformations,
  PracticePathNarrationDuringRecording
} from "../classes/model/practice-path";
import { BackendService } from "./common/backend.service";
import { HttpErrorHandlerService } from "./common/http-error-handler.service";
import { PracticeCity } from "../classes/model/practice-city";
import { NamedFile } from "./common/aws-s3.service";
import { S3 } from "aws-sdk";
import { VideoComposerService } from "./video-composer.service";
import { FinalVideo, PracticeVideo } from "../classes/model/practice-video";
import {
  DirectBucketUploadError,
  DirectBucketUploadProgress,
  DirectBucketUploadRequest,
  DirectBucketUploadResult,
  DirectBucketUploadService
} from "./common/direct-bucket-upload.service";
import { getHttpErrorStatus, getServerErrorCode, isPermanentHttpClientError, isTransientHttpError } from "./common/http-error-utils";
import { TemporaryFileUploadPurpose, TemporaryFileUploadPurposeString } from "../classes/model/temporary-file-upload-purpose";
import { awaitFor } from "../classes/misc";


@Injectable({
  providedIn: "root",
})
export class PracticePathService {
  private readonly practicePathApiUrlFragment: string =
    "/api/admin/practice_path";
  private readonly practicePathsApiUrlFragment: string =
    "/api/admin/practice-paths";
  private readonly videoUploadJobsApiUrlFragment: string =
    "/api/admin/practice-path-video-upload-jobs";
  private readonly changeEditStatesApiUrlFragment: string =
    "/api/admin/practice_path/change_edit_state";
  private readonly selectPracticePathsApiPath: string =
  "/api/admin/practice_path/select";

  private readonly narrationsOfRecordingFileArrayName: string = "narrationsOfRecording";

  private readonly maxPracticePathVideoSizeInBytes: number = 64 * 1024 * 1024 * 1024;
  private readonly videoUploadJobPollIntervalMs: number = 3000;
  // Polling gives up if the backend is unreachable for this long (or the job takes longer than the max wait)
  private readonly maxVideoUploadJobUnreachableMs: number = 1000 * 60 * 5;
  private readonly maxVideoUploadJobWaitMs: number = 1000 * 60 * 60 * 2;
  // The path is refetched after the saved video with retries for about 75 s (5 + 10 + 15 + 20 + 25 s)
  private readonly maxPracticePathRefetchAttempts: number = 6;
  private readonly practicePathRefetchRetryDelayMs: number = 5000;
  // A claim of an upload without a job can be taken over after the retryAfterSeconds of the 409 response
  // (the backend sends it only while the claim is younger than 2 minutes). The margin covers the rounding and
  // the network delays, the cap guards against waiting for an unreasonable value.
  private readonly claimTakeoverRetryMarginMs: number = 5000;
  private readonly maxClaimTakeoverWaitMs: number = 1000 * 60 * 3;

  constructor(
    private backendService: BackendService,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private s3Service: AwsS3Service,
    private videoComposerService:VideoComposerService,
    private directBucketUploadService:DirectBucketUploadService
  ) { }

  private getApiUrlPath(cityUuid:string, practicePathUuid?:string):string {
    let apiUrlFragment:string = `/api/admin/practice_path_city/${cityUuid}/practice_paths`;

    if(practicePathUuid) {
      apiUrlFragment += `/${practicePathUuid}`;
    }

    return apiUrlFragment;
  }

  // Csak és kizárólag a GeoTaggedFrames-t updateli
  // nincs side-effect, a backend csak kicseréli a path tömböt az újra
  // Igazzal tér vissza, ha sikerült a frissítés
  public async updateGeoTaggedFramesPath(
    practicePathUuid: string,
    newPath: Readonly<GeoTaggedFrame[]>
  ) {
    try {
      const response: {
        data: PracticePath;
        criticalPoints: Array<CriticalPoint>;
      } = await this.backendService.callApi(
        this.practicePathApiUrlFragment + `/${practicePathUuid}/update_path`,
        "POST",
        { newPath: newPath }
      );

      return true;
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(
        error,
        "Hiba az útvonal pontok módosításakor."
      );
      return false;
    }
  }

  /**
   * Return the practice paths of the city. If the mode is set to 'summary', only the base informations are returned.
   *
   * @param cityUuid uuid of the city
   * @param mode the mode of the query ('summary' or 'all')
   *
   * @returns array of practice paths or their base informations based in the set query mode
   */
  fetchPracticePathsOfCity(cityUuid:string, mode?:"all"):Promise<Array<PracticePath>>
  fetchPracticePathsOfCity(cityUuid:string, mode?:"summary"):Promise<Array<PracticePathBaseInformations>>
  fetchPracticePathsOfCity(cityUuid:string, mode?:"all"|"summary"):Promise<Array<PracticePath>>|Promise<Array<PracticePathBaseInformations>>
  public async fetchPracticePathsOfCity(
    cityUuid:string,
    mode:"all"|"summary" = "all"
  ):Promise<Array<PracticePath>|Array<PracticePathBaseInformations>> {
    const apiUrlFragment:string = `${this.getApiUrlPath(cityUuid)}?mode=${mode}`;
    let practicePaths:Array<PracticePath>|Array<PracticePathBaseInformations> = [];

    try {
      const response:{ practicePaths:Array<PracticePath>|Array<PracticePathBaseInformations> } = await this.backendService.callApi(
        apiUrlFragment,
        "GET"
      );
      practicePaths = response.practicePaths;
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba a vizsgahelyszín útvonalainak lekérdezése közben.");
    }

    return practicePaths;
  }

  fetchPracticePath(pathUuid:string, mode?:"all"):Promise<PracticePath>
  fetchPracticePath(pathUuid:string, mode?:"summary"):Promise<PracticePathBaseInformations>
  fetchPracticePath(pathUuid:string, mode?:"all"|"summary"):Promise<PracticePath>|Promise<PracticePathBaseInformations>
  public async fetchPracticePath(
    pathUuid:string,
    mode:"all"|"summary" = "all"
  ) {
    const apiUrlFragment:string = `${this.practicePathApiUrlFragment}/${pathUuid}?mode=${mode}`;

    try {
      const response:{ practicePath: PracticePath } = await this.backendService.callApi(apiUrlFragment, "GET");
      return response.practicePath;
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(error, "Hiba az útvonal lekérése közben");
    }
  }

  public async fetchPracticePathSetFields(practicePathUuids:Array<string>, fields:ReadonlyArray<keyof PracticePath>):Promise<PracticePathQueryResult> {
    const apiPath:string = `${this.selectPracticePathsApiPath}?fields=${fields.join(",")}`;
    const httpBody:Object = {
      practicePathUuids: practicePathUuids
    }

    let result:PracticePathQueryResult;

    try {
      const response:PracticePathQueryResult = await this.backendService.callApi(apiPath, "POST", httpBody);
      result = response;
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba az útvonalak feloldása közben.");
    }

    return result;
  }

  public async addPracticePathToCity(
    cityUuid:string,
    newPracticePath: {
      name: string;
      zoneUuids: string[];
      isFullPracticePath: boolean;
      nodeTouches?: { nodeUuid: string; touchMs: number }[];
      examPathNumber?: number;
      isFree: boolean
    }
  ): Promise<PracticePath> {
    const httpBody = {
      data: {
        item: newPracticePath,
      },
    };

    try {
      const response: { practicePath: PracticePath } = await this.backendService.callApi(
        this.getApiUrlPath(cityUuid),
        "PUT",
        httpBody
      );

      Object.assign<Partial<PracticePath>, Partial<PracticePath>>(
        newPracticePath,
        response.practicePath
      );
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(
        error,
        "Hiba az útvonal létrehozása közben."
      );
    }

    return newPracticePath as PracticePath;
  }

  // (1) A videót közvetlenül a bucketbe töltjük fel (presigned multipart URL-ekkel, a backend kihagyásával)
  // (2) Átadjuk az ideiglenes feltöltés azonosítóját a backendnek, ami háttérfeladatként menti a videót az útvonalhoz
  // (3) A feladat végéig lekérdezzük az állapotát, majd újra lekérjük az útvonalat
  // Ha a feltöltés megszakad, ugyanannak a fájlnak az újbóli feltöltése onnan folytatja, ahol abbamaradt.
  // Ha a feltöltéshez már tartozik feladat (pl. egy korábbi kérésre adott válasz elveszett), annak az állapotát követjük.
  // Ha ez a feladat már a mostani próbálkozás előtt lezárult, a videót korábban mentették el: ez nem sikeres feltöltés.
  // Null-lal tér vissza, ha a videó mentése sikerült, de a frissített útvonalat nem sikerült lekérni.
  public async uploadFullRawPracticePathVideo(
    practicePathUuid:string,
    file: NamedFile,
    gpsMetadata: GeoTaggedFrame[],
    onProgress: PracticePathVideoUploadProgressCallback,
    cancelSignal?: AbortSignal
  ):Promise<PracticePath | null> {
    // A job of a reused upload that completed before this moment saved the video in an earlier attempt
    const attemptStartedAt: number = Date.now();
    if (!this.isValidGpsMetadata(gpsMetadata)) {
      this.fail(new Error("Invalid GPS metadata of the raw video"), "A metadata fájl GPS adatai hibásak.");
    }

    const uploadRequest: DirectBucketUploadRequest = this.createVideoUploadRequest(
      TemporaryFileUploadPurpose.PracticePathRawVideo,
      practicePathUuid,
      file,
      onProgress,
      cancelSignal
    );
    const uploadResult: DirectBucketUploadResult = await this.uploadVideoToBucket(
      uploadRequest,
      "Hiba történt a nyers videó feltöltése közben."
    );

    onProgress("save", 100);
    const job: VideoUploadJob = await this.startVideoUploadJob(
      `${this.practicePathsApiUrlFragment}/${practicePathUuid}/raw-video`,
      {
        temporaryFileUploadId: uploadResult.temporaryFileUploadId,
        originalVideoName: file.name,
        gpsMetadataOfVideo: gpsMetadata.map((frame: GeoTaggedFrame) => ({
          position: { latitude: frame.position.latitude, longitude: frame.position.longitude },
          millisecOfFrame: frame.millisecOfFrame,
        })),
      },
      uploadRequest,
      uploadResult,
      "Hiba a nyers videó mentése közben."
    );
    await this.waitForVideoUploadJob(job, uploadRequest, attemptStartedAt, "A nyers videó feldolgozása nem sikerült.");

    return await this.fetchPracticePathAfterVideoUpload<PracticePath>(practicePathUuid, "all");
  }

  /**
   * Uploads a final video for a practice path. The video is uploaded straight to the bucket,
   * then the server saves it to the practice path in a background job.
   *
   * @param practicePathUuid the uuid of the target practice path
   * @param file the uploaded videofile
   * @param onProgress callback on the upload progress
   * @param cancelSignal aborting it cancels the upload (while the file is being uploaded)
   *
   * @returns the updated video field of the practice path (with the new final video), or null if the
   * video was saved, but the updated practice path could not be fetched
   */
  public async uploadFinalPracticePathVideo(
    practicePathUuid:string,
    file: NamedFile,
    onProgress: PracticePathVideoUploadProgressCallback,
    cancelSignal?: AbortSignal
  ):Promise<PracticeVideo | null> {
    // A job of a reused upload that completed before this moment saved the video in an earlier attempt
    const attemptStartedAt: number = Date.now();
    const uploadRequest: DirectBucketUploadRequest = this.createVideoUploadRequest(
      TemporaryFileUploadPurpose.PracticePathFinalVideo,
      practicePathUuid,
      file,
      onProgress,
      cancelSignal
    );
    const uploadResult: DirectBucketUploadResult = await this.uploadVideoToBucket(
      uploadRequest,
      "Hiba történt a végleges videó feltöltése közben."
    );

    onProgress("save", 100);
    const job: VideoUploadJob = await this.startVideoUploadJob(
      `${this.practicePathsApiUrlFragment}/${practicePathUuid}/final-videos`,
      {
        temporaryFileUploadId: uploadResult.temporaryFileUploadId,
        originalVideoName: file.name,
      },
      uploadRequest,
      uploadResult,
      "Hiba a végleges videó mentése közben."
    );
    await this.waitForVideoUploadJob(job, uploadRequest, attemptStartedAt, "A végleges videó feldolgozása nem sikerült.");

    const practicePath: PracticePathBaseInformations | null =
      await this.fetchPracticePathAfterVideoUpload<PracticePathBaseInformations>(practicePathUuid, "summary");
    return practicePath?.video ?? null;
  }

  private createVideoUploadRequest(
    intendedPurpose: TemporaryFileUploadPurposeString,
    practicePathUuid: string,
    file: NamedFile,
    onProgress: PracticePathVideoUploadProgressCallback,
    cancelSignal?: AbortSignal
  ): DirectBucketUploadRequest {
    return {
      file: file.file,
      originalFileName: file.name,
      contentType: "video/mp4",
      intendedPurpose: intendedPurpose,
      scopeKey: practicePathUuid,
      signal: cancelSignal,
      onProgress: (progress: DirectBucketUploadProgress) => {
        onProgress("upload", Math.floor((progress.uploadedBytes / progress.totalBytes) * 100), progress);
      },
    };
  }

  private async uploadVideoToBucket(
    uploadRequest: DirectBucketUploadRequest,
    errorMessage: string
  ): Promise<DirectBucketUploadResult> {
    if (!uploadRequest.originalFileName.toLowerCase().endsWith(".mp4")) {
      this.fail(new Error("The video is not an .mp4 file"), "A videónak .mp4 kiterjesztésűnek kell lennie.");
    }

    if (uploadRequest.file.size > this.maxPracticePathVideoSizeInBytes) {
      this.fail(new Error("The video is too large"), "A videó legfeljebb 64 GB lehet.");
    }

    try {
      return await this.directBucketUploadService.upload(uploadRequest);
    } catch (error: any) {
      if (DirectBucketUploadError.isCancellation(error)) {
        throw error;
      }

      this.fail(error, this.describeHttpError(errorMessage, error));
    }
  }

  // Hands the uploaded video over to the backend, returns the background job saving it
  private async startVideoUploadJob(
    apiUrlFragment: string,
    httpBody: Object,
    uploadRequest: DirectBucketUploadRequest,
    uploadResult: DirectBucketUploadResult,
    errorMessage: string
  ): Promise<VideoUploadJob> {
    let response: { jobId: string };
    try {
      response = await this.requestVideoUploadJobStart(apiUrlFragment, httpBody, uploadResult);
    } catch (error: any) {
      // The upload was handed over earlier (e.g. the response to that request was lost): its job is followed instead
      const existingJobId: string | null = this.getExistingJobIdOfUpload(error);
      if (existingJobId != null) {
        console.info(`The video upload already has a job (${existingJobId}), following it`);
        return { jobId: existingJobId, isExistingJobOfReusedUpload: uploadResult.isReusedCompletedUpload };
      }

      if (this.isTemporaryUploadUnusableAfterFailedSave(error, uploadResult)) {
        this.directBucketUploadService.forgetSavedUpload(uploadRequest);
      }

      this.fail(error, this.describeVideoSaveError(errorMessage, error));
    }

    // The upload is claimed by the job now, but its resume state is kept until the job completes:
    // the server releases the claim when the job fails for good, so the next attempt can reuse the upload
    if (!response?.jobId) {
      this.fail(new Error("Missing jobId in the response"), errorMessage);
    }

    return { jobId: response.jobId, isExistingJobOfReusedUpload: false };
  }

  // A reused upload is claimed, but it has no job: an earlier start request of it (e.g. the one that failed with a
  // network error while the backend was restarted) may have died between claiming it and creating the job. Instead
  // of uploading the video again, the request is repeated (once) when the backend says it can take over that claim.
  // Without retryAfterSeconds no retry can succeed (the upload was used long ago), the error is thrown at once.
  private async requestVideoUploadJobStart(
    apiUrlFragment: string,
    httpBody: Object,
    uploadResult: DirectBucketUploadResult
  ): Promise<{ jobId: string }> {
    const request = (): Promise<{ jobId: string }> =>
      this.backendService.callApi(apiUrlFragment, "POST", httpBody, undefined, undefined, 1000 * 60 * 2);

    try {
      return await request();
    } catch (error: any) {
      const takeoverWaitMs: number | null = this.getClaimTakeoverWaitMs(error);
      if (!uploadResult.isReusedCompletedUpload || takeoverWaitMs == null) {
        throw error;
      }

      console.info(`The reused video upload is claimed without a job, retrying in ${takeoverWaitMs} ms when the claim can be taken over`);
      await awaitFor(takeoverWaitMs);
      return await request();
    }
  }

  // 409 for an upload claimed without a job by a request that may have died: the body tells after how many
  // seconds a retry takes the claim over
  private getClaimTakeoverWaitMs(error: any): number | null {
    const retryAfterSeconds: unknown = error?.error?.retryAfterSeconds;
    const isClaimedWithoutJob: boolean =
      getHttpErrorStatus(error) === 409 &&
      getServerErrorCode(error) === "TEMPORARY_FILE_UPLOAD_ALREADY_CONSUMED" &&
      this.getExistingJobIdOfUpload(error) == null;

    if (!isClaimedWithoutJob || typeof retryAfterSeconds !== "number" || !(retryAfterSeconds > 0)) {
      return null;
    }

    return Math.min(retryAfterSeconds * 1000, this.maxClaimTakeoverWaitMs) + this.claimTakeoverRetryMarginMs;
  }

  // 409 for an upload that already has a job: the body has the id of the job while it exists
  private getExistingJobIdOfUpload(error: any): string | null {
    const errorCode: string | undefined = getServerErrorCode(error);
    const isJobOfThisUpload: boolean =
      errorCode === "TEMPORARY_FILE_UPLOAD_ALREADY_CONSUMED" || errorCode === "PRACTICE_PATH_VIDEO_UPLOAD_JOB_IN_PROGRESS";
    const jobId: unknown = error?.error?.jobId;

    return getHttpErrorStatus(error) === 409 && isJobOfThisUpload && typeof jobId === "string" && jobId.length > 0
      ? jobId
      : null;
  }

  // A rejected save keeps the uploaded video once, so after fixing the problem (e.g. the metadata) it is
  // reused without uploading it again. An already claimed upload, or a reused upload that is rejected
  // again, is forgotten, so the next attempt uploads the video again.
  // An upload rejected because another video of the path is being processed is always kept: the server did
  // not use it, so choosing the same file again after that job reuses it.
  private isTemporaryUploadUnusableAfterFailedSave(error: any, uploadResult: DirectBucketUploadResult): boolean {
    if (getServerErrorCode(error) === "PRACTICE_PATH_HAS_RUNNING_VIDEO_UPLOAD_JOB") {
      return false;
    }

    if (getHttpErrorStatus(error) === 409) {
      return true;
    }

    return isPermanentHttpClientError(error) && uploadResult.isReusedCompletedUpload;
  }

  // Resolves when the job completed. The resume state of the upload is forgotten only then: after a failed
  // job (or if its outcome is unknown) choosing the same file again reuses the upload.
  private async waitForVideoUploadJob(
    job: VideoUploadJob,
    uploadRequest: DirectBucketUploadRequest,
    attemptStartedAt: number,
    failureMessage: string
  ): Promise<void> {
    const { jobId } = job;
    const startedAt: number = Date.now();
    let unreachableSince: number | null = null;
    // A job seen pending or processing completes during this attempt, whatever the clocks of the client and the server say
    let hasSeenUnfinishedJob: boolean = false;

    while (true) {
      await awaitFor(this.videoUploadJobPollIntervalMs);

      let jobStatus: PracticePathVideoUploadJobStatus;
      try {
        jobStatus = await this.fetchVideoUploadJobStatus(jobId);
        unreachableSince = null;
      } catch (error: any) {
        // Tolerate network errors and deploys of the backend for a while
        unreachableSince ??= Date.now();
        if (isTransientHttpError(error) && Date.now() - unreachableSince < this.maxVideoUploadJobUnreachableMs) {
          continue;
        }

        this.fail(error, "Nem sikerült lekérdezni a videó feldolgozásának állapotát. Később frissítsd az oldalt.");
      }

      if (jobStatus.status === "completed") {
        // The video is saved, the next upload of the same file has to start a new upload
        this.directBucketUploadService.forgetSavedUpload(uploadRequest);
        if (job.isExistingJobOfReusedUpload && !hasSeenUnfinishedJob) {
          this.failIfCompletedBeforeAttempt(jobStatus, attemptStartedAt);
        }
        return;
      }

      if (jobStatus.status === "failed") {
        const errorCode: string | undefined = jobStatus.error?.code;
        this.fail(
          new Error(`Practice path video upload job ${jobId} failed: ${errorCode} ${jobStatus.error?.message}`),
          errorCode ? `${failureMessage} (Hibakód: ${errorCode})` : failureMessage
        );
      }

      hasSeenUnfinishedJob = true;
      if (Date.now() - startedAt > this.maxVideoUploadJobWaitMs) {
        this.fail(
          new Error(`Practice path video upload job ${jobId} is still ${jobStatus.status}`),
          "A videó feldolgozása még tart. Később frissítsd az oldalt."
        );
      }
    }
  }

  // The saved upload of a file stays reusable after its job completed if the page did not see that (e.g. it was closed
  // meanwhile, or the response to the start request was lost). Choosing the same file again later then gets the old
  // completed job of the upload, which saved the video back then: nothing is saved now, so that must not look like a new
  // upload, but it must not ask for an unnecessary re-upload either (the video may be exactly what the admin wanted saved).
  // A job that completes after this attempt started (e.g. the response to an earlier request of it was lost) is a success.
  // The clocks of the server and the client are compared only for a job that was already completed when it was first seen.
  private failIfCompletedBeforeAttempt(jobStatus: PracticePathVideoUploadJobStatus, attemptStartedAt: number): void {
    const finishedAt: number = Date.parse(jobStatus.finishedAt ?? "");
    if (Number.isNaN(finishedAt) || finishedAt >= attemptStartedAt) {
      return;
    }

    const finishedAtText: string = new Date(finishedAt).toLocaleString("hu-HU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    this.fail(
      new Error(`The reused video upload was already saved by a job that completed at ${jobStatus.finishedAt}`),
      `Ezt a videót már elmentették ehhez az útvonalhoz (${finishedAtText}), most nem kellett újra feltölteni. Ha még nem látod, frissítsd az oldalt. Ha mégis újra fel szeretnéd tölteni (pl. az útvonal visszaállításához), válaszd ki újra ugyanazt a fájlt: ekkor a videó elölről feltöltődik.`
    );
  }

  private async fetchVideoUploadJobStatus(jobId: string): Promise<PracticePathVideoUploadJobStatus> {
    try {
      return await this.backendService.callApi<Object, PracticePathVideoUploadJobStatus>(
        `${this.videoUploadJobsApiUrlFragment}/${encodeURIComponent(jobId)}`,
        "GET",
        undefined,
        undefined,
        undefined,
        1000 * 20
      );
    } catch (error: any) {
      // BackendService rejects every successful response that has an `error` field (like a failed job)
      if (isPracticePathVideoUploadJobStatus(error?.error)) {
        return error.error;
      }
      throw error;
    }
  }

  // The video is already saved at this point, so a failed refetch must not look like a failed upload
  // (that would invite uploading the video again): it is retried, then null is returned
  private async fetchPracticePathAfterVideoUpload<T extends PracticePath | PracticePathBaseInformations>(
    practicePathUuid: string,
    mode: "all" | "summary"
  ): Promise<T | null> {
    for (let attempt = 1; ; attempt++) {
      try {
        const response: { practicePath: T } = await this.backendService.callApi(
          `${this.practicePathApiUrlFragment}/${practicePathUuid}?mode=${mode}`,
          "GET"
        );
        return response?.practicePath ?? null;
      } catch (error: any) {
        if (attempt >= this.maxPracticePathRefetchAttempts || !isTransientHttpError(error)) {
          console.error("Could not fetch the practice path after its video was saved", error);
          return null;
        }
        await awaitFor(this.practicePathRefetchRetryDelayMs * attempt);
      }
    }
  }

  private isValidGpsMetadata(gpsMetadata: ReadonlyArray<GeoTaggedFrame>): boolean {
    return Array.isArray(gpsMetadata) && gpsMetadata.every((frame: GeoTaggedFrame) =>
      Number.isFinite(frame?.position?.latitude) &&
      Number.isFinite(frame?.position?.longitude) &&
      Number.isFinite(frame?.millisecOfFrame)
    );
  }

  private describeVideoSaveError(errorMessage: string, error: any): string {
    switch (getHttpErrorStatus(error)) {
      case 403:
        return "Nincs jogosultságod videót feltölteni ehhez az útvonalhoz.";
      case 404:
        return "Az útvonal vagy a feltöltött videó nem található.";
      case 409:
        if (getServerErrorCode(error) === "PRACTICE_PATH_HAS_RUNNING_VIDEO_UPLOAD_JOB") {
          return "Ehhez az útvonalhoz éppen egy másik videó feldolgozása folyik. Néhány perc múlva próbáld újra ugyanazzal a fájllal: a videót nem kell újra feltölteni.";
        }
        return "Ez a feltöltés már fel lett használva. Frissítsd az oldalt; ha a videó nem jelent meg, töltsd fel újra.";
      default:
        return this.describeHttpError(errorMessage, error);
    }
  }

  private describeHttpError(errorMessage: string, error: any): string {
    if (error instanceof DirectBucketUploadError) {
      return `${errorMessage} ${error.message}`;
    }

    const status: number | undefined = getHttpErrorStatus(error);
    if (status === 401) {
      return "A munkamenet lejárt. Kérjük jelentkezz be újra.";
    }
    if (status === 403) {
      return "Nincs megfelelő jogosultságod a művelethez.";
    }

    const errorCode: string | undefined = getServerErrorCode(error);
    if (errorCode) {
      return `${errorMessage} (Hibakód: ${errorCode})`;
    }
    if (status === 0) {
      return `${errorMessage} Ellenőrizd az internetkapcsolatot.`;
    }
    return errorMessage;
  }

  // Shows the error message and rethrows the error (handleError always throws)
  private fail(error: any, message: string): never {
    this.httpErrorHandlerService.handleError(error, message);
    throw error;
  }

  public async deletePracticePath(practicePathUuid:string): Promise<void> {
    const apiUrlFragment:string = `${this.practicePathApiUrlFragment}/${practicePathUuid}`;

    try {
      await this.backendService.callApi(apiUrlFragment, "DELETE");
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(
        error,
        "Hiba az útvonal törlése közben."
      );
    }
  }

  // Visszatér a backend a legfrisebb practice path-el a modify után
  public async modifyPracticePath(
    practicePathUuid:string,
    updatedPracticePathValues: {
      name: string;
      editState: string;
      isFullPracticePath: boolean;
      nodeTouches?: { nodeUuid: string; touchMs: number }[];
      examPathNumber?: number;
      zoneUuids: string[],
      isFree: boolean
    }
  ): Promise<PracticePath> {
    const apiUrlFragment:string = `${this.practicePathApiUrlFragment}/${practicePathUuid}`;

    const httpBody = {
      item: updatedPracticePathValues,
    };

    try {
      const response: { updatedPracticePath: PracticePath } = await this.backendService.callApi(
        apiUrlFragment,
        "POST",
        httpBody
      );

      return response.updatedPracticePath;
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(
        error,
        "Hiba az útvonal módosítása közben."
      );
    }
  }

  // Több kritikus pontnak egyszerre megváltoztatja az edit state-et
  // ha például változott egy kritikus pont, ami benne van x videóban
  // akkor ezeket a videókat átállítjuk 'Ellenőrzésre vár'-ra
  public async changeEditStates(
    practicePaths: ReadonlyArray<PracticePath>,
    newEditState: PathEditState
  ): Promise<void> {
    const httpBody: Object = {
      practicePathUuids: practicePaths.map((practicePath) => practicePath.uuid),
      newEditState: newEditState,
    };

    try {
      await this.backendService.callApi(
        this.changeEditStatesApiUrlFragment,
        "POST",
        httpBody
      );
      practicePaths.forEach((practicePath: PracticePath) => {
        practicePath.editState = newEditState;
      });
    } catch (error: any) {
      this.httpErrorHandlerService.handleError(
        error,
        "Az érintett útvonalak állapotának frissítése nem sikerült."
      );
    }
  }

  /**
   * Returns the number of the non-verified critical points of the practice path.
   *
   * @param practicePath the practice path
   *
   * @returns the number of the non-verified critical points
   */
  public getNumberOfNonVerifiedCriticalPointsOfPath(
    practiceCity:PracticeCity,
    practicePath:PracticePath|PracticePathBaseInformations
  ):number {
    // Filter all critical point assignents of which the corresponding critical point is not verified
    // and return the resulting array's length
    return practicePath.criticalPointAssignments.filter(
      (criticalPointAssignment:CriticalPointAssignment) => {
        // Get the corresponding critical point from the city
        const criticalPoint:CriticalPoint = practiceCity.criticalPoints.find(
          criticalPoint => criticalPoint.uuid === criticalPointAssignment.criticalPointUuid
        );

        // If the critical point not exist, the integrity is damaged
        if(criticalPoint == undefined) {
          return false;
        }

        return !criticalPoint.isVerified;
      }
    ).length;
  }

  /**
   * Returns the number of critical points without any stop points in the given practice path.
   *
   * @param practicePath the target practice path
   *
   * @returns the number of critical points without stop point
   */
  public getNumberOfCriticalPointsWithoutStopPointOfPath(practicePath:PracticePath|PracticePathBaseInformations):number {
    return practicePath.criticalPointAssignments.filter(
      (criticalPointAssignment:CriticalPointAssignment) => {
        return criticalPointAssignment.stopPoints.length === 0;
      }
    ).length;
  }


  public async uploadNarrationsDuringRecordingToPracticePath(
    practicePathUuid:string,
    titlesOfFiles:Array<{ fileName:string, title?:string }>,
    files:Array<File>,
    onUploadProgress:(loaded: number, total?: number) => void
  ):Promise<Array<PracticePathNarrationDuringRecording>> {
    const apiUrlPath:string = `${this.practicePathApiUrlFragment}/${practicePathUuid}/upload-narrations-during-recording`;

    const httpBody = {
      titlesOfFiles: titlesOfFiles
    }

    const fileMap:Map<string, Array<File>> = new Map<string, Array<File>>();
    fileMap.set(this.narrationsOfRecordingFileArrayName, files);

    try {
      const response:{ narrationsDuringRecording:Array<PracticePathNarrationDuringRecording> } = await this.backendService.callApi(
        apiUrlPath,
        "POST",
        httpBody,
        fileMap,
        {
          onUploadProgress: onUploadProgress
        },
        1000 * 60 * 60 * 2
      );

      return response.narrationsDuringRecording;
    } catch(error:any) {
      this.httpErrorHandlerService.handleError(error, "Hiba a narrációs fájlok feltöltése közben");
    }
  }

}

export type PracticePathVideoUploadProgressCallback = (
  state: "upload" | "save",
  progress: number,
  // Only for the "upload" state
  details?: DirectBucketUploadProgress
) => void;

export type PracticePathVideoUploadJobStatus = {
  status: "pending" | "processing" | "completed" | "failed";
  error?: { code: string; message: string };
  // ISO date, only for completed and failed jobs
  finishedAt?: string;
};

// A job found through a 409 for a reused upload may be the job of an earlier attempt that saved the video long ago
type VideoUploadJob = {
  jobId: string;
  isExistingJobOfReusedUpload: boolean;
};

function isPracticePathVideoUploadJobStatus(value: any): value is PracticePathVideoUploadJobStatus {
  return ["pending", "processing", "completed", "failed"].includes(value?.status);
}

export type PracticePathQueryResult = {
  practicePaths:Array<Partial<PracticePath>&Pick<PracticePath, "uuid">>;
  notFoundPracticePathUuids:Array<string>;
}
